/**
 * Weight Assignment Task Component
 * 
 * Allows a participant to assign relative weights to criteria.
 * Weights determine how important each criterion is in the final prioritization.
 * Supports multiple weighting methods: equal, manual slider, pairwise.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Save,
  CheckCircle,
  AlertCircle,
  Scale,
  Equal,
  Sliders,
  RotateCcw,
} from 'lucide-react';
import type {
  PrioritizationTask,
  WeightAssignmentResponse,
} from '../../../types/prioritization/prioritizationTask';
import { usePrioritizationTaskStore } from '../../../stores/prioritization/prioritizationTaskStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';

// ============================================================================
// Types
// ============================================================================

export interface WeightAssignmentTaskProps {
  task: PrioritizationTask;
  userId: string;
  userName: string;
  onComplete?: () => void;
  /** Pre-selected criteria IDs (from previous criteria selection task) */
  criteriaIds?: string[];
}

type WeightingMethod = 'equal' | 'manual' | 'pairwise';

// ============================================================================
// Main Component
// ============================================================================

export function WeightAssignmentTask({ 
  task, 
  userId, 
  userName, 
  onComplete,
  criteriaIds = [],
}: WeightAssignmentTaskProps) {
  // State
  const [method, setMethod] = useState<WeightingMethod>('manual');
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get attributes from store
  const allAttributes = useAttributesStore(state => state.attributes);
  const criteria = useMemo(() => {
    if (criteriaIds.length > 0) {
      return criteriaIds
        .map(id => allAttributes.find(a => a.id === id))
        .filter((a): a is NonNullable<typeof a> => !!a);
    }
    // Fall back to all numeric/rating attributes
    return allAttributes.filter(a => 
      a.type === 'number' || 
      a.type === 'rating'
    );
  }, [criteriaIds, allAttributes]);
  
  // Initialize equal weights
  useEffect(() => {
    if (criteria.length > 0 && Object.keys(weights).length === 0) {
      const equalWeight = Math.round(100 / criteria.length);
      const initialWeights: Record<string, number> = {};
      criteria.forEach((c, i) => {
        // Adjust last one to ensure total is 100
        if (i === criteria.length - 1) {
          const currentTotal = Object.values(initialWeights).reduce((sum, w) => sum + w, 0);
          initialWeights[c.id] = 100 - currentTotal;
        } else {
          initialWeights[c.id] = equalWeight;
        }
      });
      setWeights(initialWeights);
    }
  }, [criteria, weights]);
  
  // Store actions
  const submitResponse = usePrioritizationTaskStore(state => state.submitResponse);
  
  // Check if already submitted
  const existingResponse = task.responses.find(r => r.participantId === userId);
  
  // Calculate total weight
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const isValid = Math.abs(totalWeight - 100) < 0.01;
  
  const handleWeightChange = (criterionId: string, newWeight: number) => {
    setWeights(prev => ({ ...prev, [criterionId]: newWeight }));
  };
  
  const handleEqualizeWeights = () => {
    const equalWeight = Math.round(100 / criteria.length);
    const newWeights: Record<string, number> = {};
    criteria.forEach((c, i) => {
      if (i === criteria.length - 1) {
        const currentTotal = Object.values(newWeights).reduce((sum, w) => sum + w, 0);
        newWeights[c.id] = 100 - currentTotal;
      } else {
        newWeights[c.id] = equalWeight;
      }
    });
    setWeights(newWeights);
    setMethod('equal');
  };
  
  const handleResetWeights = () => {
    handleEqualizeWeights();
  };
  
  const handleSubmit = async () => {
    if (!isValid) return;
    
    setIsSubmitting(true);
    
    try {
      const responseData: WeightAssignmentResponse = {
        type: 'weight-assignment',
        weights,
        method,
      };
      
      submitResponse({
        taskId: task.id,
        participantId: userId,
        participantName: userName,
        data: responseData,
      });
      
      onComplete?.();
    } catch (error) {
      console.error('[WeightAssignmentTask] Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show existing response if already submitted
  if (existingResponse) {
    const data = existingResponse.data as WeightAssignmentResponse;
    return (
      <div className="space-y-6">
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4">
          <div className="flex items-center gap-2 text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Weights assigned</span>
          </div>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
            You assigned weights on {new Date(existingResponse.submittedAt).toLocaleString()}.
          </p>
        </div>
        
        <div className="bg-[var(--bg-raised)] border divide-y">
          <div className="px-4 py-3 bg-[var(--surface)] font-medium text-[var(--text-sec)]">
            Your Weight Assignments
          </div>
          {Object.entries(data.weights).map(([id, weight]) => {
            const attr = allAttributes.find(a => a.id === id);
            return (
              <div key={id} className="px-4 py-3 flex items-center justify-between">
                <span className="text-[var(--text-pri)]">{attr?.name || id}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary-500 rounded-full"
                      style={{ width: `${weight}%` }}
                    />
                  </div>
                  <span className="font-medium text-primary-600 w-12 text-right">
                    {weight}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <Scale className="h-12 w-12 mx-auto text-[var(--green)] mb-4" />
        <h2 className="text-xl font-semibold text-[var(--text-pri)]">
          Assign Criteria Weights
        </h2>
        <p className="text-[var(--text-sec)] mt-2">
          Determine how important each criterion is relative to others.
          Weights should sum to 100%.
        </p>
      </div>
      
      {/* Method Selection */}
      <div className="flex justify-center gap-2">
        <button
          onClick={handleEqualizeWeights}
          className={`flex items-center gap-2 px-4 py-2  border transition-colors ${
            method === 'equal' 
              ? 'bg-primary-100 border-primary-500 text-primary-700' 
              : 'bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
          }`}
        >
          <Equal className="h-4 w-4" />
          Equal
        </button>
        <button
          onClick={() => setMethod('manual')}
          className={`flex items-center gap-2 px-4 py-2  border transition-colors ${
            method === 'manual' 
              ? 'bg-primary-100 border-primary-500 text-primary-700' 
              : 'bg-[var(--bg-raised)] hover:bg-[var(--surface)]'
          }`}
        >
          <Sliders className="h-4 w-4" />
          Manual
        </button>
      </div>
      
      {/* Total Indicator */}
      <div className={`text-center py-3  ${
        isValid 
          ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' 
          : totalWeight > 100 
            ? 'bg-[var(--coral-dim)] text-[var(--coral)]'
            : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
      }`}>
        <span className="font-medium">Total: {totalWeight}%</span>
        {!isValid && (
          <span className="ml-2">
            {totalWeight > 100 ? `(${totalWeight - 100}% over)` : `(${100 - totalWeight}% remaining)`}
          </span>
        )}
      </div>
      
      {/* Weight Sliders */}
      <div className="bg-[var(--bg-raised)] border divide-y">
        {criteria.map(criterion => (
          <div key={criterion.id} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-[var(--text-pri)]">{criterion.name}</span>
              <span className="text-sm font-medium text-[var(--text-sec)] w-12 text-right">
                {weights[criterion.id] || 0}%
              </span>
            </div>
            
            <input
              type="range"
              min="0"
              max="100"
              value={weights[criterion.id] || 0}
              onChange={(e) => {
                handleWeightChange(criterion.id, Number(e.target.value));
                setMethod('manual');
              }}
              className="w-full h-2 bg-[var(--hover-tint)] appearance-none cursor-pointer accent-primary-500"
            />
            
            {criterion.description && (
              <p className="text-xs text-[var(--text-ter)] mt-1">{criterion.description}</p>
            )}
          </div>
        ))}
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleResetWeights}
          className="flex items-center gap-2 text-[var(--text-sec)] hover:text-[var(--text-pri)]"
        >
          <RotateCcw className="h-4 w-4" />
          Reset to Equal
        </button>
        
        <button
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <Save className="h-4 w-4" />
              Submit Weights
            </>
          )}
        </button>
      </div>
      
      {!isValid && (
        <div className="flex items-center gap-2 text-[var(--mustard)] text-sm">
          <AlertCircle className="h-4 w-4" />
          Weights must sum to exactly 100%
        </div>
      )}
    </div>
  );
}

export default WeightAssignmentTask;
